import { useState, useEffect, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { RotateCw } from "lucide-react"

import { useBalance } from "@/entities/user"
import { usePvpRoomsStore } from "@/entities/pvp-roulette"

import { getRuntimeConfig } from "@/shared/config"
import {
  usePvpWheelBootstrapStore,
  useRoomBootstrapStore,
} from "@/shared/store"
import { useI18n } from "@/shared/i18n"
import Button from "@/shared/ui/Button"

interface AppLoaderProps {
  children: React.ReactNode
}

const STEP_IDS = [
  "session",
  "snapshot",
  "balance",
  "wheel",
  "shell",
] as const
type StepId = (typeof STEP_IDS)[number]

const STEP_WEIGHTS: Record<StepId, number> = {
  session: 14,
  snapshot: 36,
  balance: 21,
  wheel: 13,
  shell: 16,
}

const STEP_LABEL_KEYS: Record<StepId, string> = {
  session: "appLoader.connecting",
  snapshot: "appLoader.loadingRoom",
  balance: "appLoader.fetchingBalance",
  wheel: "appLoader.wheelPixi",
  shell: "appLoader.shellAssets",
}

interface LoadStep {
  weight: number
  done: boolean
}

const SHELL_BACKGROUND_URLS = [
  "/images/loader-background.png",
  "/images/pvp-background.png",
] as const

function preloadImages(urls: readonly string[]): Promise<void> {
  return Promise.all(
    urls.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image()
          img.onload = () => resolve()
          img.onerror = () => resolve()
          img.src = src
        })
    )
  ).then(() => {})
}

/** Prod: no snapshot → recovery UI instead of entering the shell without round data. */
const ROOM_BOOTSTRAP_DEADLINE_MS = 12_000
/**
 * `npm run dev`: no real room snapshot / balance — don’t block on dead network; unlock shell instead of recovery UI.
 */
const ROOM_BOOTSTRAP_DEADLINE_DEV_MS = 800

/** First paint URL — `AppLoader` sits outside `<Router>`, so we only use the cold-load path. */
function initialPathnameNeedsWheelPixi(): boolean {
  if (typeof window === "undefined") return false
  const p = window.location.pathname
  return p === "/" || p === "" || p.startsWith("/dev/room-emulator")
}

export function AppLoader({ children }: AppLoaderProps) {
  const { t } = useI18n()

  const needsWheelPixiBootstrap = useRef(initialPathnameNeedsWheelPixi())

  const pixiInitDone = usePvpWheelBootstrapStore((s) => s.pixiInitDone)

  const [steps, setSteps] = useState<Record<StepId, LoadStep>>(() =>
    Object.fromEntries(
      STEP_IDS.map((id) => [id, { weight: STEP_WEIGHTS[id], done: false }])
    ) as Record<StepId, LoadStep>
  )
  const [ready, setReady] = useState(false)
  const [fadeOut, setFadeOut] = useState(false)
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false)

  const config = getRuntimeConfig()
  const roomId = config.pvpRoomId ?? "default"
  const hasViewState = usePvpRoomsStore((s) => !!s.rooms[roomId]?.viewState)
  const subscriptionInitFailed = useRoomBootstrapStore(
    (s) => s.subscriptionInitFailed
  )

  const bootstrapDeadlineMs = import.meta.env.DEV
    ? ROOM_BOOTSTRAP_DEADLINE_DEV_MS
    : ROOM_BOOTSTRAP_DEADLINE_MS

  const markDone = useCallback((key: StepId) => {
    setSteps((prev) => {
      if (prev[key]?.done) return prev
      return { ...prev, [key]: { ...prev[key], done: true } }
    })
  }, [])

  // Session is considered ready immediately (ensureSession called in App)
  useEffect(() => {
    const t0 = setTimeout(() => markDone("session"), 300)
    return () => clearTimeout(t0)
  }, [markDone])

  useEffect(() => {
    if (hasViewState) {
      setBootstrapTimedOut(false)
      useRoomBootstrapStore.getState().setSubscriptionInitFailed(false)
      return
    }
    const t = window.setTimeout(
      () => setBootstrapTimedOut(true),
      bootstrapDeadlineMs
    )
    return () => window.clearTimeout(t)
  }, [hasViewState, roomId, bootstrapDeadlineMs])

  // Mark snapshot step done when first viewState for the room appears in the store
  useEffect(() => {
    if (hasViewState) {
      markDone("snapshot")
    }
  }, [hasViewState, markDone])

  // Balance loading
  const { isFetched: balanceFetched, isError: balanceError } = useBalance()

  useEffect(() => {
    if (balanceFetched || balanceError) {
      markDone("balance")
    }
  }, [balanceFetched, balanceError, markDone])

  useEffect(() => {
    if (!needsWheelPixiBootstrap.current) {
      markDone("wheel")
    }
  }, [markDone])

  useEffect(() => {
    if (!needsWheelPixiBootstrap.current) return
    if (pixiInitDone) {
      markDone("wheel")
    }
  }, [pixiInitDone, markDone])

  useEffect(() => {
    let cancelled = false
    preloadImages(SHELL_BACKGROUND_URLS).then(() => {
      if (!cancelled) markDone("shell")
    })
    return () => {
      cancelled = true
    }
  }, [markDone])

  /**
   * Dev: never trap behind “Reload” — API/WS/Telegram often absent. Fake-complete bootstrap so UI is usable
   * (e.g. `/dev/room-emulator`). Prod still requires real snapshot + balance (or balance error).
   */
  const devShellUnlock =
    import.meta.env.DEV &&
    !hasViewState &&
    (subscriptionInitFailed || bootstrapTimedOut)

  useEffect(() => {
    if (!devShellUnlock) return
    markDone("snapshot")
    markDone("balance")
    markDone("wheel")
    markDone("shell")
  }, [devShellUnlock, markDone])

  // Calculate progress
  const totalWeight = Object.values(steps).reduce((s, st) => s + st.weight, 0)
  const doneWeight = Object.values(steps)
    .filter((s) => s.done)
    .reduce((s, st) => s + st.weight, 0)
  const progress = Math.round((doneWeight / totalWeight) * 100)

  const currentStepId = STEP_IDS.find((id) => !steps[id].done)
  const currentLabel = currentStepId
    ? t(STEP_LABEL_KEYS[currentStepId])
    : t("appLoader.ready")

  const showRecovery =
    !import.meta.env.DEV &&
    !hasViewState &&
    (subscriptionInitFailed || bootstrapTimedOut)

  // All done → fade out
  useEffect(() => {
    if (progress >= 100 && !ready) {
      setFadeOut(true)
      const t0 = setTimeout(() => setReady(true), 400)
      return () => clearTimeout(t0)
    }
  }, [progress, ready])

  return (
    <>
      {/* Block interaction with routed shell until bootstrap finishes (overlay is still primary cover). */}
      <div
        className={ready ? undefined : "pointer-events-none"}
        aria-hidden={!ready}
      >
        {children}
      </div>
      <AnimatePresence>
        {!ready && (
          <motion.div
            key="loader"
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-8 bg-background bg-loader-background bg-cover bg-center px-6"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <AnimatePresence mode="wait">
              {!showRecovery ? (
                <motion.div
                  key="loading"
                  className="flex flex-col items-center gap-8"
                  initial={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  animate={{ opacity: fadeOut ? 0 : 1 }}
                  exit={{ opacity: 0, scale: 0.9, filter: "blur(6px)" }}
                  transition={{ duration: 0.38, ease: [0.4, 0, 0.2, 1] }}
                >
                  <div className="w-52 space-y-3">
                    <div className="relative h-6 w-full rounded-full bg-black-64">
                      <motion.div
                        className="h-full rounded-full bg-[#FF9500] shadow-[0_0_10px_rgba(255,187,0,1)]"
                        initial={{ width: "0%" }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      />
                      <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm/none font-black text-white [text-shadow:_0.5px_0_0.5px_black,_-0.5px_0_0.5px_black,_0px_-0.5px_0.5px_black,_0px_1px_0.5px_black]">
                        {progress}%
                      </p>
                    </div>
                    <p className="text-center text-xl/none font-black text-white [text-shadow:_0.5px_0_0.5px_black,_-0.5px_0_0.5px_black,_0px_-0.5px_0.5px_black,_0px_1px_0.5px_black]">
                      {currentLabel}
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="recovery"
                  className="flex w-full max-w-[320px] flex-col items-center gap-5 text-center"
                  initial={{ opacity: 0, scale: 0.88, y: 16 }}
                  animate={{ opacity: fadeOut ? 0 : 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.div
                    initial={{ rotate: -40, scale: 0.6 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 22,
                      delay: 0.05,
                    }}
                    className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/[0.08] ring-1 ring-white/10"
                  >
                    <RotateCw className="h-8 w-8 text-green-100 drop-shadow-[0_0_18px_rgba(177,254,100,0.35)]" />
                  </motion.div>
                  <div className="space-y-2">
                    <h2 className="text-balance text-lg font-semibold leading-snug text-white">
                      {t("appLoader.loadFailedTitle")}
                    </h2>
                    <p className="text-balance text-sm leading-relaxed text-white/60">
                      {t("appLoader.loadFailedHint")}
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    size="medium"
                    appearance="green"
                    onClick={() => {
                      window.location.reload()
                    }}
                  >
                    {t("appLoader.reloadPage")}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
